"use client";

import { useState, useEffect } from "react";
import { track } from "@vercel/analytics";

// Order: west to east, following the sun
const TIMEZONES = [
  { zone: "America/Los_Angeles", label: "LA" },
  { zone: "America/New_York", label: "BOS" },
  { zone: "America/Argentina/Buenos_Aires", label: "BA" },
  { zone: "Europe/Berlin", label: "BER" },
  { zone: "Asia/Tokyo", label: "TYO" },
];

interface ClockProps {
  className?: string;
  style?: React.CSSProperties;
  brandColor?: string;
  brandColorEnd?: string;
  onTimezoneChange?: (isBOS: boolean) => void;
}

export function Clock({ className, style, brandColor, brandColorEnd, onTimezoneChange }: ClockProps) {
  const [tzIndex, setTzIndex] = useState(1); // Default to BOS
  const [time, setTime] = useState({ hours: "", minutes: "", seconds: "", dayOffset: 0 });
  const [colonVisible, setColonVisible] = useState(true);
  const [isPressed, setIsPressed] = useState(false);

  const currentTz = TIMEZONES[tzIndex];

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeStr = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: currentTz.zone,
      });
      const [hours, minutes, seconds] = timeStr.split(":");

      // Calculate day offset between timezone and local (handles month boundaries)
      const localDate = new Date(now.toLocaleDateString("en-US", { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
      const tzDate = new Date(now.toLocaleDateString("en-US", { timeZone: currentTz.zone }));
      const dayOffset = Math.round((tzDate.getTime() - localDate.getTime()) / (1000 * 60 * 60 * 24));

      setTime({ hours, minutes, seconds, dayOffset });
      setColonVisible(true);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [currentTz.zone]);

  // Blink colons off halfway through each second
  useEffect(() => {
    const blinkTimeout = setTimeout(() => setColonVisible(false), 500);
    return () => clearTimeout(blinkTimeout);
  }, [time.seconds]);

  const cycleTimezone = () => {
    setIsPressed(true);
    const nextIndex = (tzIndex + 1) % TIMEZONES.length;
    setTzIndex(nextIndex);
    track("clock_click", { timezone: TIMEZONES[nextIndex].label });
    onTimezoneChange?.(nextIndex === 1); // BOS is index 1
    setTimeout(() => setIsPressed(false), 150);
  };

  // When brandColor is provided, use inline gradient styles
  const gradientStyle: React.CSSProperties | undefined = brandColor ? {
    backgroundImage: `linear-gradient(90deg, ${brandColor}, ${brandColorEnd || brandColor}, ${brandColor})`,
    backgroundSize: "200% 100%",
    backgroundClip: "text",
    WebkitBackgroundClip: "text",
    color: "transparent",
    animation: "gradient-x 4s linear infinite",
  } : undefined;

  const colonStyle: React.CSSProperties = {
    opacity: colonVisible ? 1 : 0.4,
    transition: "opacity 0.1s ease",
  };

  return (
    <span
      className={className}
      style={{
        ...style,
        ...gradientStyle,
        cursor: "pointer",
        transform: isPressed ? "scale(1.05)" : "scale(1)",
        transition: "transform 0.15s ease-out",
        display: "inline-block",
      }}
      onClick={cycleTimezone}
      title={`Click to change timezone (${currentTz.zone})`}
    >
      {time.hours}
      <span style={colonStyle}>:</span>
      {time.minutes}
      <span style={colonStyle}>:</span>
      {time.seconds}
      {" "}{currentTz.label}
      {time.dayOffset !== 0 && ` (${time.dayOffset > 0 ? "+" : ""}${time.dayOffset})`}
    </span>
  );
}
